<script setup lang="ts">
import { computed, ref, useId } from 'vue'

import './base.css'
import { useFieldContext } from './field-context'
import type { Size } from './types'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    type?: 'text' | 'email' | 'password' | 'search' | 'tel' | 'url' | 'date' | 'time'
    size?: Size
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    /** Right-aligned, tabular figures. Use for quantities and money entry. */
    numeric?: boolean
    /** Forces the invalid styling when the control is used outside `EField`. */
    invalid?: boolean
    autocomplete?: string
    inputmode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' | 'search'
    maxlength?: number
    /** Emit on every keystroke instead of on change. Off for money entry. */
    live?: boolean
  }>(),
  { type: 'text', size: 'md', placeholder: '', autocomplete: 'off', live: true },
)

const emit = defineEmits<{
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
  enter: [event: KeyboardEvent]
}>()

const model = defineModel<string>({ default: '' })

const field = useFieldContext()
const fallbackId = useId()
const input = ref<HTMLInputElement | null>(null)

const id = computed(() => field?.controlId.value ?? fallbackId)
const describedBy = computed(() => field?.describedBy.value)
const isInvalid = computed(() => props.invalid === true || field?.invalid.value === true)
const isRequired = computed(() => field?.required.value === true)
const isDisabled = computed(() => props.disabled === true || field?.disabled.value === true)

function onInput(event: Event): void {
  if (!props.live) return
  model.value = (event.target as HTMLInputElement).value
}

function onChange(event: Event): void {
  if (props.live) return
  model.value = (event.target as HTMLInputElement).value
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') emit('enter', event)
}

defineExpose({
  /** Focus the control — used by forms that jump to the first invalid field. */
  focus: (options?: FocusOptions) => input.value?.focus(options),
  select: () => input.value?.select(),
})
</script>

<template>
  <div class="e-input" :class="{ 'e-input--sm': size === 'sm' }">
    <span v-if="$slots.prefix" class="e-input__affix e-input__affix--prefix">
      <slot name="prefix" />
    </span>

    <input
      :id="id"
      ref="input"
      v-bind="$attrs"
      class="e-control"
      :class="{ 'e-control--sm': size === 'sm', 'e-control--numeric': numeric }"
      :type="type"
      :value="model"
      :placeholder="placeholder === '' ? undefined : placeholder"
      :disabled="isDisabled"
      :readonly="readonly"
      :required="isRequired || undefined"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      :maxlength="maxlength"
      :aria-invalid="isInvalid ? 'true' : undefined"
      :aria-describedby="describedBy"
      :aria-required="isRequired ? 'true' : undefined"
      @input="onInput"
      @change="onChange"
      @keydown="onKeydown"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
    />

    <span v-if="$slots.suffix" class="e-input__affix e-input__affix--suffix">
      <slot name="suffix" />
    </span>
  </div>
</template>

<style>
.e-input {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
}

.e-input__affix {
  position: absolute;
  display: inline-flex;
  align-items: center;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-sm);
  pointer-events: none;
}

.e-input__affix--prefix {
  inset-inline-start: var(--erpx-space-2);
}

.e-input__affix--suffix {
  inset-inline-end: var(--erpx-space-2);
}

.e-input:has(.e-input__affix--prefix) .e-control {
  padding-inline-start: var(--erpx-space-8);
}

.e-input:has(.e-input__affix--suffix) .e-control {
  padding-inline-end: var(--erpx-space-8);
}
</style>
